import React, { useRef, useEffect } from 'react'
import { useFormikContext } from 'formik'
import get from 'lodash.get'
import set from 'lodash.set'
import { styled } from '@mui/material/styles'

import Box from '@mui/material/Box'
import TextField from '@mui/material/TextField'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import Checkbox from '@mui/material/Checkbox'
import MenuItem from '@mui/material/MenuItem'
import Grid from '@mui/material/Grid'
import Collapse from '@mui/material/Collapse'

import AddressAutocomplete from 'components/common/AddressAutocomplete'

import { DAYFORCE_AGENCY_PREFERENCES_SORT_ORDER } from 'utils/constants'

const AutoResizeWrapper = styled('div')({
  position: 'relative',
  '& > textarea': {
    resize: 'none',
    overflow: 'hidden',
  },
  '&::after': {
    content: 'attr(data-replicated-value) " "',
    whiteSpace: 'pre-wrap',
    visibility: 'hidden',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    padding: 'inherit',
    border: 'inherit',
    boxSizing: 'inherit',
    font: 'inherit',
    lineHeight: 'inherit',
    wordWrap: 'break-word',
  },
})

function AutoResizeTextField({ value = '', disabled = false, ...props }) {
  const wrapperRef = useRef(null)

  useEffect(() => {
    if (wrapperRef.current) {
      wrapperRef.current.setAttribute('data-replicated-value', value)
    }
  }, [value])

  return (
    <AutoResizeWrapper ref={wrapperRef}>
      <TextField
        {...props}
        multiline
        value={value}
        disabled={disabled}
        inputProps={{
          style: { resize: 'none', overflow: 'hidden' },
        }}
      />
    </AutoResizeWrapper>
  )
}

const AddressSelect = ({
  name,
  label,
  mailSeparately,
  setMailSeparately,
  disabled = false,
}) => {
  const { values, handleChange, errors, setErrors, setFieldValue } =
    useFormikContext()

  const handleUpdateChange = (e) => {
    handleChange(e)
    setErrors({
      ...errors,
      [e.target.name]: undefined,
    })
  }

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Typography>Mail Separately from Checks</Typography>
        <Checkbox
          label="Mail Separately from Checks"
          value={mailSeparately}
          checked={mailSeparately}
          onChange={(_, checked) => {
            setMailSeparately(checked)

            if (!checked) {
              setFieldValue(name, '')
            }
          }}
          name={`${name}.mail_with_checks`}
          size="small"
          disabled={disabled}
        />
      </Box>
      {mailSeparately && (
        <Box mt={3}>
          <TextField
            required
            value={get(values, name)}
            onChange={handleUpdateChange}
            name={name}
            label={label}
            select
            fullWidth
            size="small"
            placeholder={label}
            error={Boolean(errors?.[name])}
            helperText={errors?.[name]}
            variant="outlined"
            disabled={disabled}
          >
            <MenuItem value="primary_mailing_address">
              Primary Mailing Address
            </MenuItem>
            <MenuItem
              disabled={!values?.includeOvernightMailingAddress}
              value="overnight_mailing_address"
            >
              Overnight Mailing Address
            </MenuItem>
            <MenuItem
              disabled={!values?.includeAlternateMailingAddress}
              value="alternate_mailing_address"
            >
              Alternate Mailing Address
            </MenuItem>
          </TextField>
        </Box>
      )}
    </>
  )
}

const AddressAutocompleteWithCollapse = ({
  show = true,
  hideLabel = false,
  disabled = false,
  ...props
}) => {
  const { values, handleChange, errors, setErrors } = useFormikContext()

  const handleUpdateChange = (e) => {
    handleChange(e)

    const newErrors = { ...errors }

    set(newErrors, e.target.name, undefined)

    setErrors(newErrors)
  }

  return (
    <Collapse in={show}>
      <Box>
        {!hideLabel && (
          <Typography variant="h2" sx={{ mb: 4 }}>
            {props.label}
          </Typography>
        )}
        <Grid container spacing={2} rowSpacing={3}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              size="small"
              variant="outlined"
              value={get(values, props.addressKeys.recipient1) || ''}
              onChange={handleChange}
              name={props.addressKeys.recipient1}
              error={Boolean(get(errors, props.addressKeys.recipient1))}
              helperText={get(errors, props.addressKeys.recipient1)}
              label="Recipient Line 1"
              required
              disabled={disabled}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              size="small"
              variant="outlined"
              value={get(values, props.addressKeys.recipient2) || ''}
              onChange={handleChange}
              name={props.addressKeys.recipient2}
              error={Boolean(get(errors, props.addressKeys.recipient2))}
              helperText={get(errors, props.addressKeys.recipient2)}
              label="Recipient Line 2"
              disabled={disabled}
            />
          </Grid>
          <Grid item xs={12}>
            <AddressAutocomplete {...props} />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              size="small"
              label="Address 2"
              variant="outlined"
              value={get(values, props.addressKeys.address2) || ''}
              onChange={handleUpdateChange}
              name={props.addressKeys.address2}
              error={Boolean(get(errors, props.addressKeys.address2))}
              helperText={get(errors, props.addressKeys.address2)}
              disabled={disabled}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              required
              fullWidth
              size="small"
              label="City"
              variant="outlined"
              value={get(values, props.addressKeys.city) || ''}
              onChange={handleUpdateChange}
              name={props.addressKeys.city}
              error={Boolean(get(errors, props.addressKeys.city))}
              helperText={get(errors, props.addressKeys.city)}
              disabled={disabled}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              required
              fullWidth
              size="small"
              label="State"
              variant="outlined"
              value={get(values, props.addressKeys.state) || ''}
              onChange={handleUpdateChange}
              name={props.addressKeys.state}
              error={Boolean(get(errors, props.addressKeys.state))}
              helperText={get(errors, props.addressKeys.state)}
              disabled={disabled}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              required
              fullWidth
              size="small"
              label="Zip"
              variant="outlined"
              value={get(values, props.addressKeys.zip) || ''}
              onChange={handleUpdateChange}
              name={props.addressKeys.zip}
              error={Boolean(get(errors, props.addressKeys.zip))}
              helperText={get(errors, props.addressKeys.zip)}
              disabled={disabled}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              required
              fullWidth
              size="small"
              label="Country"
              variant="outlined"
              value={get(values, props.addressKeys.country) || ''}
              onChange={handleUpdateChange}
              name={props.addressKeys.country}
              error={Boolean(get(errors, props.addressKeys.country))}
              helperText={get(errors, props.addressKeys.country)}
              disabled={disabled}
            />
          </Grid>
        </Grid>
      </Box>
    </Collapse>
  )
}

const MailingInformation = ({ formDisabled }) => {
  const { values, handleChange } = useFormikContext()

  return (
    <>
      <Typography variant="h1" sx={{ mb: 4 }}>
        Mailing Information
      </Typography>
      <AddressAutocompleteWithCollapse
        label="Primary Mailing Address"
        disabled={formDisabled}
        addressKeys={{
          recipient1:
            'mailing_preferences.primary_mailing_address.recipient_line_1',
          recipient2:
            'mailing_preferences.primary_mailing_address.recipient_line_2',
          address: 'mailing_preferences.primary_mailing_address.address1',
          address2: 'mailing_preferences.primary_mailing_address.address2',
          city: 'mailing_preferences.primary_mailing_address.city',
          state: 'mailing_preferences.primary_mailing_address.state',
          zip: 'mailing_preferences.primary_mailing_address.zip',
          country: 'mailing_preferences.primary_mailing_address.country',
        }}
        textFieldProps={{
          required: true,
          fullWidth: true,
          size: 'small',
          label: 'Address',
          variant: 'outlined',
          placeholder: '',
          disabled: formDisabled,
        }}
      />
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h2">
            Include Overnight Mailing Address
          </Typography>
          <Checkbox
            label="Include Overnight Mailing Address"
            value={values.includeOvernightMailingAddress || false}
            checked={values.includeOvernightMailingAddress || false}
            onChange={handleChange}
            name="includeOvernightMailingAddress"
            size="small"
            disabled={formDisabled}
          />
        </Box>
        <AddressAutocompleteWithCollapse
          show={values.includeOvernightMailingAddress}
          hideLabel
          label="Overnight Mailing Address"
          disabled={formDisabled}
          addressKeys={{
            recipient1:
              'mailing_preferences.overnight_mailing_address.recipient_line_1',
            recipient2:
              'mailing_preferences.overnight_mailing_address.recipient_line_2',
            address: 'mailing_preferences.overnight_mailing_address.address1',
            address2: 'mailing_preferences.overnight_mailing_address.address2',
            city: 'mailing_preferences.overnight_mailing_address.city',
            state: 'mailing_preferences.overnight_mailing_address.state',
            zip: 'mailing_preferences.overnight_mailing_address.zip',
            country: 'mailing_preferences.overnight_mailing_address.country',
          }}
          textFieldProps={{
            required: true,
            fullWidth: true,
            size: 'small',
            label: 'Address',
            variant: 'outlined',
            placeholder: '',
            disabled: formDisabled,
          }}
        />
        {values.includeOvernightMailingAddress && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography>Always Ship Overnight</Typography>
            <Checkbox
              label="Always Ship Overnight"
              value={
                values?.mailing_preferences?.always_ship_overnight || false
              }
              checked={
                values?.mailing_preferences?.always_ship_overnight || false
              }
              onChange={handleChange}
              name="mailing_preferences.always_ship_overnight"
              size="small"
              disabled={formDisabled}
            />
          </Box>
        )}
      </Box>
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h2">
            Include Alternate Mailing Address
          </Typography>
          <Checkbox
            label="Include Alternate Mailing Address"
            value={values.includeAlternateMailingAddress || false}
            checked={values.includeAlternateMailingAddress || false}
            onChange={handleChange}
            name="includeAlternateMailingAddress"
            size="small"
            disabled={formDisabled}
          />
        </Box>
        <AddressAutocompleteWithCollapse
          show={values.includeAlternateMailingAddress}
          hideLabel
          label="Alternate Mailing Address"
          disabled={formDisabled}
          addressKeys={{
            recipient1:
              'mailing_preferences.alternate_mailing_address.recipient_line_1',
            recipient2:
              'mailing_preferences.alternate_mailing_address.recipient_line_2',
            address: 'mailing_preferences.alternate_mailing_address.address1',
            address2: 'mailing_preferences.alternate_mailing_address.address2',
            city: 'mailing_preferences.alternate_mailing_address.city',
            state: 'mailing_preferences.alternate_mailing_address.state',
            zip: 'mailing_preferences.alternate_mailing_address.zip',
            country: 'mailing_preferences.alternate_mailing_address.country',
          }}
          textFieldProps={{
            required: true,
            fullWidth: true,
            size: 'small',
            label: 'Address',
            variant: 'outlined',
            placeholder: '',
            disabled: formDisabled,
          }}
        />
      </Box>
    </>
  )
}

const ZeroCoupons = ({ formDisabled }) => {
  const { values, handleChange } = useFormikContext()

  return (
    <Box>
      <Typography variant="h1" sx={{ mb: 4 }}>
        Zero Coupons
      </Typography>
      <Stack>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography>Send</Typography>
          <Checkbox
            label="Send"
            value={values?.mailing_preferences?.zero_coupons?.send || false}
            checked={values?.mailing_preferences?.zero_coupons?.send || false}
            onChange={handleChange}
            name="mailing_preferences.zero_coupons.send"
            size="small"
            disabled={formDisabled}
          />
        </Box>
      </Stack>
    </Box>
  )
}

const AppliedFors = ({ formDisabled }) => {
  const { values, handleChange, setFieldValue } = useFormikContext()

  return (
    <Box>
      <Typography variant="h1" sx={{ mb: 4 }}>
        Applied Fors
      </Typography>
      <Stack>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography>Send</Typography>
          <Checkbox
            label="Send"
            value={values?.mailing_preferences?.applied_fors?.send || false}
            checked={values?.mailing_preferences?.applied_fors?.send || false}
            onChange={handleChange}
            name="mailing_preferences.applied_fors.send"
            size="small"
            disabled={formDisabled}
          />
        </Box>
        <Collapse in={values?.mailing_preferences?.applied_fors?.send}>
          <Box mt={2}>
            <AddressSelect
              name="mailing_preferences.applied_fors.mailing_address"
              label="Mailing Address"
              disabled={formDisabled}
              mailSeparately={
                !get(
                  values,
                  'mailing_preferences.applied_fors.mail_with_checks'
                )
              }
              setMailSeparately={() =>
                setFieldValue(
                  'mailing_preferences.applied_fors.mail_with_checks',
                  !get(
                    values,
                    'mailing_preferences.applied_fors.mail_with_checks'
                  )
                )
              }
            />
          </Box>
        </Collapse>
      </Stack>
    </Box>
  )
}

const Reports = ({ formDisabled }) => {
  const { values, handleChange, setFieldValue } = useFormikContext()

  return (
    <Box>
      <Typography variant="h1" sx={{ mb: 4 }}>
        Reports
      </Typography>
      <Stack>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography>Generate Reports</Typography>
          <Checkbox
            label="Generate Reports"
            value={
              values?.mailing_preferences?.reports?.generate_reports || false
            }
            checked={
              values?.mailing_preferences?.reports?.generate_reports || false
            }
            onChange={handleChange}
            name="mailing_preferences.reports.generate_reports"
            size="small"
            disabled={formDisabled}
          />
        </Box>
        <Collapse in={values?.mailing_preferences?.reports?.generate_reports}>
          <Box mt={2}>
            <AddressSelect
              name="mailing_preferences.reports.mailing_address"
              label="Mailing Address"
              disabled={formDisabled}
              mailSeparately={
                !get(values, 'mailing_preferences.reports.mail_with_checks')
              }
              setMailSeparately={() =>
                setFieldValue(
                  'mailing_preferences.reports.mail_with_checks',
                  !get(values, 'mailing_preferences.reports.mail_with_checks')
                )
              }
            />
          </Box>
        </Collapse>
      </Stack>
    </Box>
  )
}

const CD = ({ formDisabled }) => {
  const { values, handleChange, setFieldValue } = useFormikContext()

  return (
    <Box>
      <Typography variant="h1" sx={{ mb: 4 }}>
        CD
      </Typography>
      <Stack>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography>Generate CD</Typography>
          <Checkbox
            label="Generate CD"
            value={values?.mailing_preferences?.cd?.generate_cd || false}
            checked={values?.mailing_preferences?.cd?.generate_cd || false}
            onChange={handleChange}
            name="mailing_preferences.cd.generate_cd"
            size="small"
            disabled={formDisabled}
          />
        </Box>
        <Collapse in={values?.mailing_preferences?.cd?.generate_cd}>
          <Box mt={2}>
            <AddressSelect
              name="mailing_preferences.cd.mailing_address"
              label="Mailing Address"
              disabled={formDisabled}
              mailSeparately={
                !get(values, 'mailing_preferences.cd.mail_with_checks')
              }
              setMailSeparately={() =>
                setFieldValue(
                  'mailing_preferences.cd.mail_with_checks',
                  !get(values, 'mailing_preferences.cd.mail_with_checks')
                )
              }
            />
          </Box>
        </Collapse>
      </Stack>
    </Box>
  )
}

const SpecialHandling = ({ formDisabled }) => {
  const { values, handleChange, errors, touched } = useFormikContext()

  return (
    <Box>
      <Typography variant="h1" sx={{ mb: 4 }}>
        Special Handling
      </Typography>
      <Stack>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography>Requires Special Handling</Typography>
          <Checkbox
            label="Special Handling"
            value={
              values?.mailing_preferences?.special_handling?.required || false
            }
            checked={
              values?.mailing_preferences?.special_handling?.required || false
            }
            onChange={handleChange}
            name="mailing_preferences.special_handling.required"
            size="small"
            disabled={formDisabled}
          />
        </Box>
        <Collapse in={values?.mailing_preferences?.special_handling?.required}>
          <Box mt={2}>
            <TextField
              value={
                values?.mailing_preferences?.special_handling?.instructions ||
                ''
              }
              onChange={handleChange}
              name="mailing_preferences.special_handling.instructions"
              label="Enter any special handling instructions necessary for print production"
              disabled={formDisabled}
              multiline
              rows={3}
              maxRows={3}
              fullWidth
              size="small"
              variant="filled"
              error={Boolean(
                get(
                  touched,
                  'mailing_preferences.special_handling.instructions'
                ) &&
                  get(
                    errors,
                    'mailing_preferences.special_handling.instructions'
                  )
              )}
              helperText={
                get(
                  touched,
                  'mailing_preferences.special_handling.instructions'
                ) &&
                get(errors, 'mailing_preferences.special_handling.instructions')
              }
            />
          </Box>
        </Collapse>
      </Stack>
    </Box>
  )
}

const AgencyPackage = ({ formDisabled }) => {
  const { values, handleChange, touched, errors } = useFormikContext()

  return (
    <Box>
      <Typography variant="h1" sx={{ mb: 4 }}>
        Checks and Coupons Agency Package
      </Typography>
      <Stack spacing={5}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography>Hold Checks to Due Date</Typography>
          <Checkbox
            label="Hold Checks to Due Date"
            value={
              values?.mailing_preferences?.agency_package
                ?.hold_checks_to_due_date || false
            }
            checked={
              values?.mailing_preferences?.agency_package
                ?.hold_checks_to_due_date || false
            }
            onChange={handleChange}
            name="mailing_preferences.agency_package.hold_checks_to_due_date"
            size="small"
            disabled={formDisabled}
          />
        </Box>
        <Box>
          <TextField
            value={
              values?.mailing_preferences?.agency_package?.sort_order || ''
            }
            onChange={handleChange}
            name="mailing_preferences.agency_package.sort_order"
            label="Sort Order"
            select
            fullWidth
            size="small"
            disabled={formDisabled}
            error={Boolean(
              get(touched, 'mailing_preferences.agency_package.sort_order') &&
                get(errors, 'mailing_preferences.agency_package.sort_order')
            )}
            helperText={
              get(touched, 'mailing_preferences.agency_package.sort_order') &&
              get(errors, 'mailing_preferences.agency_package.sort_order')
            }
            variant="outlined"
          >
            {Object.entries(DAYFORCE_AGENCY_PREFERENCES_SORT_ORDER).map(
              ([key, value]) => (
                <MenuItem key={key} value={key}>
                  {value}
                </MenuItem>
              )
            )}
          </TextField>
        </Box>
      </Stack>
    </Box>
  )
}

const AdditionalRequirements = ({ formDisabled }) => {
  const { values, handleChange } = useFormikContext()

  return (
    <Box>
      <Typography variant="h1" sx={{ mb: 4 }}>
        Additional Requirements
      </Typography>
      <AutoResizeTextField
        value={values?.mailing_preferences?.additional_requirements || ''}
        onChange={handleChange}
        name="mailing_preferences.additional_requirements"
        placeholder="Enter any special requirements or notes related to this tax code"
        minRows={4}
        fullWidth
        size="small"
        variant="filled"
        disabled={formDisabled}
      />
    </Box>
  )
}

function DailyMonthlyCnCForm() {
  const { errors } = useFormikContext()
  const formDisabled = errors?.tax_code === 'Tax code already exists'

  return (
    <>
      <MailingInformation formDisabled={formDisabled} />
      <ZeroCoupons formDisabled={formDisabled} />
      <AppliedFors formDisabled={formDisabled} />
      <Reports formDisabled={formDisabled} />
      <CD formDisabled={formDisabled} />
      <SpecialHandling formDisabled={formDisabled} />
      <AgencyPackage formDisabled={formDisabled} />
      <AdditionalRequirements formDisabled={formDisabled} />
    </>
  )
}

export default DailyMonthlyCnCForm
